<template>
  <v-dialog
    :model-value="modelValue"
    :max-width="fullscreen ? undefined : widthPx"
    :fullscreen="fullscreen"
    :persistent="persistent"
    :scrollable="scrollable"
    :content-class="['app-dialog', contentClass]"
    :retain-focus="true"
    @update:model-value="onModelValue"
    @keydown.esc="onEsc"
    @after-enter="focusFirst"
  >
    <v-card class="app-dialog__card" role="dialog" aria-modal="true" :aria-labelledby="titleId">
      <slot name="header">
        <header v-if="title || !hideClose" class="app-dialog__header">
          <div class="app-dialog__heading">
            <span v-if="icon" class="app-dialog__icon">
              <v-icon :icon="icon" size="22" aria-hidden="true" />
            </span>
            <div class="app-dialog__titles">
              <h2 :id="titleId" class="app-dialog__title">{{ title }}</h2>
              <p v-if="subtitle" class="app-dialog__subtitle">{{ subtitle }}</p>
            </div>
          </div>
          <v-btn
            v-if="!hideClose"
            icon="mdi-close"
            variant="text"
            size="small"
            class="app-dialog__close"
            :disabled="loading"
            :aria-label="t('actions.close')"
            @click="close"
          />
        </header>
      </slot>

      <div ref="bodyRef" class="app-dialog__body">
        <slot />
      </div>

      <footer v-if="$slots.actions" class="app-dialog__actions">
        <slot name="actions" />
      </footer>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed, ref, useId } from 'vue'
import { useI18n } from 'vue-i18n'
import { useBreakpoints } from '@/composables/useBreakpoints'

/**
 * Diálogo base de la app.
 *
 * Existía en tres generaciones distintas repartidas en 34 v-dialog: once anchos
 * diferentes, tres formas de enlazar v-model, ningún `fullscreen` en móvil,
 * `persistent` puesto justo al revés de lo razonable (protegía el buscador y no
 * los formularios largos) y prácticamente nada de accesibilidad.
 *
 * Aquí eso se decide una vez:
 *  - Cuatro anchos: sm 440 · md 560 · lg 760 · xl 1080.
 *  - En móvil ocupa la pantalla completa (salvo `fullscreenOnMobile = false`).
 *  - Esc siempre cierra, incluso con `persistent`, que solo evita el cierre
 *    accidental al pulsar fuera. Mientras `loading` esté activo no cierra nada.
 *  - role/aria-modal/aria-labelledby y foco al primer elemento del cuerpo.
 */
const props = defineProps({
  modelValue: { type: Boolean, default: false },
  title: { type: String, default: '' },
  subtitle: { type: String, default: '' },
  icon: { type: String, default: '' },
  size: {
    type: String,
    default: 'md',
    validator: (v) => ['sm', 'md', 'lg', 'xl'].includes(v),
  },
  /** Ocupa la pantalla completa en móvil. */
  fullscreenOnMobile: { type: Boolean, default: true },
  /** Evita el cierre al pulsar fuera. Úsalo si hay datos sin guardar. */
  persistent: { type: Boolean, default: false },
  scrollable: { type: Boolean, default: true },
  /** Bloquea el cierre: hay una acción en curso. */
  loading: { type: Boolean, default: false },
  hideClose: { type: Boolean, default: false },
  contentClass: { type: String, default: '' },
})

const emit = defineEmits(['update:modelValue', 'close'])

const { t } = useI18n()
const { isCompact } = useBreakpoints()
const titleId = `app-dialog-title-${useId()}`
const bodyRef = ref(null)

const SIZES = { sm: 440, md: 560, lg: 760, xl: 1080 }
const widthPx = computed(() => SIZES[props.size])
// isCompact (<600) y no isMobile (<960): el CSS de más abajo usa 600, así que
// con isMobile el diálogo era fullscreen entre 600 y 960 pero conservaba las
// esquinas y el padding de escritorio.
const fullscreen = computed(() => props.fullscreenOnMobile && isCompact.value)

function close() {
  if (props.loading) return
  emit('update:modelValue', false)
  emit('close')
}

function onModelValue(value) {
  if (value) return
  close()
}

function onEsc(event) {
  // Vuetify ya cierra con Esc cuando no es persistente; con persistent no lo
  // hace, y eso dejaba diálogos (el buscador global, sin ir más lejos) que solo
  // se cerraban con el ratón.
  if (!props.persistent) return
  event.stopPropagation()
  close()
}

function focusFirst() {
  const root = bodyRef.value
  if (!root) return
  const focusable = root.querySelector(
    'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled]), [contenteditable="true"], button:not([disabled]), a[href]'
  )
  focusable?.focus?.()
}
</script>

<style scoped>
/* El contenido de los overlays se teletransporta fuera de <v-app>, así que
   estos estilos no pueden depender de `.v-application` (ver components.css). */
.app-dialog__card.app-dialog__card {
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  border-radius: var(--ws-radius-xl);
  background: var(--ws-surface-2);
  border: 1px solid var(--ws-border);
  box-shadow: var(--ws-shadow-xl);
  overflow: hidden;
}

.app-dialog__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--ws-space-4);
  padding: var(--ws-space-6) var(--ws-space-6) var(--ws-space-4);
  flex-shrink: 0;
}

.app-dialog__heading {
  display: flex;
  align-items: center;
  gap: var(--ws-space-3);
  min-width: 0;
}

.app-dialog__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: var(--ws-radius-md);
  background: var(--ws-gradient-brand);
  color: #fff;
}

.app-dialog__titles {
  min-width: 0;
}

.app-dialog__title {
  font-size: var(--ws-text-lg);
  font-weight: var(--ws-fw-bold);
  line-height: var(--ws-leading-tight);
  color: var(--ws-text-high);
  margin: 0;
}

.app-dialog__subtitle {
  font-size: var(--ws-text-base);
  color: var(--ws-text-medium);
  margin: var(--ws-space-1) 0 0;
}

.app-dialog__close {
  flex-shrink: 0;
  color: var(--ws-text-low);
}

.app-dialog__body {
  /* El padding superior deja sitio a la etiqueta flotante del primer campo:
     con 0 se recortaba por arriba. */
  padding: var(--ws-space-2) var(--ws-space-6) var(--ws-space-6);
  overflow-y: auto;
  flex: 1 1 auto;
}

.app-dialog__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--ws-space-3);
  padding: var(--ws-space-4) var(--ws-space-6) var(--ws-space-6);
  flex-shrink: 0;
  border-top: 1px solid var(--ws-border-subtle);
  background: var(--ws-surface-2);
}

/* En móvil ocupa la pantalla completa: sin esquinas ni borde. */
@media (max-width: 600px) {
  .app-dialog__card.app-dialog__card {
    max-height: 100%;
    border-radius: 0;
    border: none;
  }

  .app-dialog__header {
    padding: var(--ws-space-4);
  }

  .app-dialog__body {
    padding: 0 var(--ws-space-4) var(--ws-space-4);
  }

  .app-dialog__actions {
    padding: var(--ws-space-4);
  }

  .app-dialog__actions :deep(.v-btn) {
    flex: 1;
  }
}
</style>
